// Engine-aware ROUTINE-identity comparator — the routine half of what
// `relationIdentity.ts`'s `matchRelationRef` does for tables and views.
//
// WHY a routine needs its own comparator and a column does not
// ----------------------------------------------------------------
// `index/identifierIndex.ts` keys a COLUMN reference BARE on both sides,
// because a column's prefix inside a body is an ALIAS (`u.email`) — buffer
// noise that would split the index across every alias spelling. A ROUTINE's
// prefix is not noise: `dbo.fn` and `sales.fn` are two different functions, so
// the schema IS part of the identity and the exact-match key keeps it.
//
// That makes the exact key the wrong DISCOVERY key, though, because the two
// sides spell the qualification differently:
//   - EMITTERS (`index/viewWalker.ts`, `index/constraintExpressionScanner.ts`,
//     the procedural walkers) record a call exactly as the body wrote it —
//     `SELECT fn(x)` → bare, `SELECT dbo.fn(x)` → schema-qualified.
//   - READERS arrive with the TARGET's qualification — an Object-Explorer
//     routine is always schema-qualified (`dbo.fn`), a cursor target is
//     whatever the user typed.
// So "Find References" on the OE node `dbo.fn` missed every body that calls
// `fn(...)` bare, and a cursor on a bare `fn(` missed every body that calls
// `dbo.fn(...)`. Relations hit this first and solved it with a broad leaf-name
// candidate lookup plus a comparator; routines now take the same route.
//
// This module owns NO index lookup and invents no fold of its own — it is a
// pure predicate over two already-parsed identities, and every component
// comparison goes through `relationIdentity.ts`'s `componentsEqual` so the
// per-engine case rules (SQL Server's per-database collation on the SPELLED
// halves, PostgreSQL / Oracle's byte-exact folded forms, MySQL's
// `lower_case_table_names`, SQLite's blanket insensitivity) cannot drift
// between relations and routines. The one fold it APPLIES is
// `oeIdentifier.ts`'s `foldOeComponent`, and only to the two CATALOG schema
// spellings that reach it without an engine fold — see `catalogSchema` below.
//
// SCOPE: 4-part linked-server calls (`srv.db.dbo.fn()`) are NOT distinguished.
// Relations carry `refPartCount` from the FROM-clause walker and flag ≥4 as
// ambiguous; the resolver's `function-call` FROM target and the constraint
// scanner do not surface a part count today, so a 4-part routine call still
// collapses to `db.schema.name` exactly as it did before this change. No
// regression, and the gap is recorded here rather than silently carried.

import type { SqlEngine } from '../sql-grammar/model';

import { foldOeComponent } from './oeIdentifier';
import {
  componentsEqual,
  qualifiedComponent,
  type IdentityComponent,
  type RelationIdentityContext,
} from './relationIdentity';
import type { MatchConfidence, QualifiedName } from './types';

export type RoutineIdentityContext = RelationIdentityContext & {
  /** The schema a BARE routine call in a body resolves to on this connection —
   *  in the engine's CATALOG spelling, so it compares like any other schema
   *  component.
   *
   *  There is exactly ONE implementation of that per-engine rule in the app,
   *  `workspace/common/utils/defaultSchemaForEngine.ts`'s
   *  `resolveDefaultSchemaForEngine(engineId, database, connectionId)`, and
   *  this field is where find-references hands its answer in. Do not re-derive
   *  it here. What that table says, and why each engine says it:
   *
   *    | engine     | a bare `fn()` resolves to                                |
   *    |------------|----------------------------------------------------------|
   *    | mssql      | `dbo` — see the note below: a bare call is legal only for |
   *    |            | table-valued functions and built-ins; either resolves via |
   *    |            | the default schema.                                       |
   *    | postgres   | `public` — the tail of the default `search_path`         |
   *    |            | (`"$user", public`).                                     |
   *    | mysql      | the connection's current DATABASE — MySQL has no schema   |
   *    |            | dimension, a "schema" IS a database.                     |
   *    | oracle     | the connection user's own schema, folded UPPER.          |
   *    | sqlite     | n/a — SQLite has no stored routines at all.              |
   *
   *  Undefined (unknown connection, a unit fixture) simply removes the
   *  default-schema half of the test; the calling object's own schema can
   *  still earn a `contextual`. */
  readonly defaultRoutineSchema?: string;
};

export function routineIdentityContextFor(
  base: RelationIdentityContext,
  defaultRoutineSchema: string | undefined,
): RoutineIdentityContext {
  return { ...base, ...(defaultRoutineSchema ? { defaultRoutineSchema } : {}) };
}

export type RoutineMatch = { readonly confidence: MatchConfidence };

/** Decide whether a recorded routine reference (`ref`) denotes `target`, and
 *  with what confidence. `null` means DEFINITELY a different routine.
 *
 *  `callerObject` is the object whose body holds the reference (the record's
 *  `objectName`) — a bare call inside `sales.v_orders` plausibly resolves to
 *  `sales.fn` wherever the engine puts the caller's schema on the resolution
 *  path, so that spelling earns `contextual` as well. Its SPELLING is the
 *  catalog's; its folded halves are the index's coarse listing key and are NOT
 *  read here — see {@link catalogSchema}.
 *
 *  Confidence vocabulary is the relation one, unchanged:
 *   - `exact`      — every component present on both sides matches (or neither
 *                    side is qualified at all, which is as specific as the
 *                    identity model gets).
 *   - `contextual` — one side is bare and the other's schema is where a bare
 *                    call on this engine/connection would land.
 *   - `ambiguous`  — one side is bare and the other's schema is neither. Kept
 *                    for discovery (find-references over-includes on purpose);
 *                    a future rewriting consumer gates on it the way rename
 *                    gates ambiguous relation matches. */
export function matchRoutineRef(
  ref: QualifiedName,
  target: QualifiedName,
  callerObject: QualifiedName | undefined,
  ctx: RoutineIdentityContext,
): RoutineMatch | null {
  // `'routine-leaf'`, not `'leaf'`: MySQL's `lower_case_table_names` governs
  // schema directories and table/view files, never the routine names inside
  // them, so the routine's own name stays case-insensitive there in every mode
  // while its SCHEMA below still follows the setting. Every other engine treats
  // the two leaves alike (see `identityCaseContext.ts`'s `IdentityComponent`).
  if (!sameComponent(ref, target, 'routine-leaf', ctx)) return null;

  // An explicit database on BOTH sides must agree; absent on either side stays
  // a wildcard, as everywhere in `relationIdentity.ts`.
  const refDb = ref.foldedDatabase ?? '';
  const targetDb = target.foldedDatabase ?? '';
  if (refDb && targetDb && !sameComponent(ref, target, 'database', ctx)) return null;

  const refSchema = ref.foldedSchema ?? '';
  const targetSchema = target.foldedSchema ?? '';
  if (refSchema && targetSchema) {
    return sameComponent(ref, target, 'schema', ctx) ? { confidence: 'exact' } : null;
  }
  if (!refSchema && !targetSchema) return { confidence: 'exact' };

  // Exactly one side carries a schema. The other is bare, so the question is
  // whether a bare call would land on the explicit one.
  //
  // MSSQL NOTE: T-SQL requires a schema-qualified call for a user SCALAR
  // function (`SELECT fn()` is a syntax error), but a table-valued function
  // in FROM (`SELECT * FROM fn(1)`) and a built-in (`SYSUTCDATETIME()`) are
  // legal bare — the real-DB routine arm relies on the TVF shape. A bare
  // TVF call resolves through the default schema, so `dbo` earns
  // `contextual` for the same reason it does on every other engine; a
  // built-in carries no schema of its own and never shares a user routine's
  // name, so the same answer costs nothing there. One table, not one per
  // engine.
  const explicit = refSchema ? ref : target;
  return resolvesBareCall(explicit, callerObject, ctx)
    ? { confidence: 'contextual' }
    : { confidence: 'ambiguous' };
}

/** True when a bare call could resolve to `explicit`'s schema: it is either the
 *  engine's default routine schema for this connection, or the schema of the
 *  object whose body spelled the call. */
function resolvesBareCall(
  explicit: QualifiedName,
  callerObject: QualifiedName | undefined,
  ctx: RoutineIdentityContext,
): boolean {
  const explicitSchema = qualifiedComponent(explicit, 'schema');
  const fallback = ctx.defaultRoutineSchema;
  if (fallback && componentsEqual(explicitSchema, catalogSchema(fallback, ctx.engine), 'schema', ctx)) {
    return true;
  }
  if (!callerObject?.foldedSchema) return false;
  const caller = qualifiedComponent(callerObject, 'schema');
  return componentsEqual(explicitSchema, catalogSchema(caller.spelled, ctx.engine), 'schema', ctx);
}

/** A CATALOG schema spelling as a comparable component — the default-routine
 *  schema the resolver handed us (`dbo`, `public`, `JAMSQL`) and the calling
 *  object's own schema, which are the same kind of value and must therefore
 *  fold the same way.
 *
 *  The SPELLED half is that catalog spelling, which is what a `_CS_` SQL Server
 *  database compares on. The folded half is re-derived with `oeIdentifier.ts`'s
 *  {@link foldOeComponent} — never a hand-rolled `toLowerCase`, and never the
 *  component's OWN `foldedSchema`.
 *
 *  WHY the caller's `foldedSchema` cannot be used: the caller is the index
 *  record's `objectName`, and every catalog feed builds that with
 *  `index/engines/queryHelper.ts`'s `makeQualifiedName`, whose folded halves
 *  are unconditionally LOWER-cased — deliberately, because they also serve as
 *  `identifierIndex.ts`'s coarse, case-insensitive bucket key. The EXPLICIT
 *  side of this comparison comes from the body walker or the Object Explorer
 *  and therefore carries the ENGINE's fold (`HR`, `"Sales"`). On PostgreSQL and
 *  Oracle `componentsEqual` is byte-exact on the folded forms, so a listing's
 *  `hr` never met a walker's `HR`: the caller half of {@link resolvesBareCall}
 *  was dead on exactly those two engines while MSSQL / MySQL / SQLite — whose
 *  engine fold IS lower-case — worked. That is a per-engine split with no
 *  engine reason behind it, which the cross-engine consistency rule forbids.
 *
 *  {@link foldOeComponent} is the fold every Object-Explorer identity already
 *  goes through, so it infers quoting from the stored case on the two engines
 *  that store it: an Oracle `HR` stays `HR`, a quoted Oracle `"hr"` stays `hr`,
 *  a PostgreSQL `"Sales"` stays `Sales` and a plain `sales` stays `sales` —
 *  which is also why an unquoted `sales` caller still does NOT answer a quoted
 *  `"Sales"` routine. On MSSQL / MySQL / SQLite it is the blanket lower-case
 *  fold, i.e. exactly what the listing already produced — nothing changes
 *  there. */
function catalogSchema(schema: string, engine: SqlEngine): { spelled: string; folded: string } {
  return { spelled: schema, folded: foldOeComponent(engine, schema) };
}

function sameComponent(
  a: QualifiedName,
  b: QualifiedName,
  part: IdentityComponent,
  ctx: RoutineIdentityContext,
): boolean {
  return componentsEqual(qualifiedComponent(a, part), qualifiedComponent(b, part), part, ctx);
}
